if (!customElements.get('waitlist-size-picker')) {
  class WaitlistSizePicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;

      const select = this.querySelector('select');
      const trigger = this.querySelector('.vcd__trigger');
      const label = this.querySelector('.vcd__trigger-label');
      const panel = this.querySelector('.waitlist-size-picker__panel');
      const error = this.querySelector('.waitlist-size-picker__error');
      const choices = Array.from(this.querySelectorAll('[data-size]'));
      const form = this.closest('form');

      if (!select || !trigger || !panel || !form) {
        requestAnimationFrame(() => this.initialize());
        return;
      }

      this.initialized = true;

      const modal = this.closest('.notify-modal');
      const legacySizeLabel = modal
        ? modal.querySelector('[id^="notify-modal-size-"]')
        : null;

      const closePanel = () => {
        if (panel.matches(':popover-open')) {
          panel.hidePopover();
        }
        trigger.setAttribute('aria-expanded', 'false');
      };

      const positionPanel = () => {
        const bounds = trigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const below = viewportHeight - bounds.bottom - 12;
        const above = bounds.top - 12;
        const openAbove = below < 180 && above > below;
        const available = openAbove ? above : below;

        panel.style.width = bounds.width + 'px';
        panel.style.maxHeight = Math.max(40, Math.min(220, available)) + 'px';
        panel.style.left = Math.max(
          8,
          Math.min(bounds.left, window.innerWidth - bounds.width - 8)
        ) + 'px';

        if (openAbove) {
          panel.style.top = 'auto';
          panel.style.bottom = viewportHeight - bounds.top + 2 + 'px';
        } else {
          panel.style.bottom = 'auto';
          panel.style.top = bounds.bottom + 2 + 'px';
        }
      };

      const update = () => {
        label.textContent = select.value || 'Select Size';
        trigger.setAttribute(
          'aria-label',
          select.value ? 'Size: ' + select.value : 'Select size'
        );
        trigger.disabled = select.disabled;

        choices.forEach((choice) => {
          choice.setAttribute(
            'aria-pressed',
            String(choice.dataset.size === select.value)
          );
        });

        if (select.disabled) closePanel();
      };

      const openPanel = () => {
        if (select.disabled) return;

        positionPanel();
        if (!panel.matches(':popover-open')) panel.showPopover();
        trigger.setAttribute('aria-expanded', 'true');
      };

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (panel.matches(':popover-open')) {
          closePanel();
        } else {
          openPanel();
        }
      });

      panel.addEventListener('toggle', () => {
        trigger.setAttribute(
          'aria-expanded',
          String(panel.matches(':popover-open'))
        );
      });

      choices.forEach((choice) => {
        choice.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          select.value = choice.dataset.size;

          if (legacySizeLabel) {
            legacySizeLabel.textContent = select.value;
          }

          error.textContent = '';
          update();
          closePanel();

          if (
            event.detail === 0 ||
            window.matchMedia('(hover: hover) and (pointer: fine)').matches
          ) {
            trigger.focus({ preventScroll: true });
          } else if (document.activeElement === trigger) {
            trigger.blur();
          } else if (document.activeElement === choice) {
            choice.blur();
          }

          select.dispatchEvent(new Event('change', { bubbles: true }));

          const ownerCard = this.closest('product-card');

          if (ownerCard) {
            ownerCard.dataset.waitlistSize = select.value;

            ownerCard.querySelectorAll('waitlist-size-picker').forEach((picker) => {
              if (picker === this) return;

              const otherSelect = picker.querySelector('select[name="size"]');
              if (
                !otherSelect ||
                otherSelect.disabled ||
                otherSelect.value === select.value
              ) return;

              const hasMatchingSize = Array.from(otherSelect.options).some(
                (option) => option.value === select.value
              );

              if (!hasMatchingSize) return;

              otherSelect.value = select.value;
              otherSelect.dispatchEvent(
                new Event('change', { bubbles: true })
              );
            });
          }
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && panel.matches(':popover-open')) {
          event.preventDefault();
          event.stopPropagation();
          closePanel();
          trigger.focus();
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

        event.preventDefault();
        event.stopPropagation();
        openPanel();

        const current = choices.indexOf(document.activeElement);
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        const next = current < 0
          ? (direction > 0 ? 0 : choices.length - 1)
          : (current + direction + choices.length) % choices.length;

        if (choices[next]) choices[next].focus();
      });

      form.addEventListener('submit', (event) => {
        closePanel();

        if (select.value) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        error.textContent = 'Please select a size.';
        trigger.focus();
      }, true);

      select.addEventListener('change', update);

      const card = this.closest('product-card');

      if (card && card.dataset.waitlistSize) {
        const savedSize = card.dataset.waitlistSize;
        const hasMatchingSize = Array.from(select.options).some(
          (option) => option.value === savedSize
        );

        if (hasMatchingSize) {
          select.value = savedSize;
        }
      }

      this.disabledObserver = new MutationObserver(update);
      this.disabledObserver.observe(select, {
        attributes: true,
        attributeFilter: ['disabled']
      });

      if (legacySizeLabel) {
        const syncLegacySize = () => {
          const value = legacySizeLabel.textContent.trim();

          select.value = choices.some(
            (choice) => choice.dataset.size === value
          ) ? value : '';

          update();
        };

        this.sizeObserver = new MutationObserver(syncLegacySize);
        this.sizeObserver.observe(legacySizeLabel, {
          childList: true,
          characterData: true,
          subtree: true
        });

        syncLegacySize();
      }

      const eventController = new AbortController();
      this.eventController = eventController;

      window.addEventListener('resize', closePanel, {
        signal: eventController.signal
      });

      document.addEventListener('scroll', (event) => {
        if (panel.contains(event.target)) return;
        closePanel();
      }, {
        capture: true,
        signal: eventController.signal
      });

      update();
    }

    disconnectedCallback() {
      if (this.isConnected) return;

      if (this.eventController) this.eventController.abort();
      if (this.disabledObserver) this.disabledObserver.disconnect();
      if (this.sizeObserver) this.sizeObserver.disconnect();
    }
  }

  customElements.define('waitlist-size-picker', WaitlistSizePicker);
}
